import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';

import { isZplContentType, type BookedLabel } from './labels';
import styles from './styles.module.css';

interface LabelPreviewProps {
  label: BookedLabel;
  getPng: (label: BookedLabel) => Promise<Blob | null>;
}

/** Loads its preview only once scrolled into view, so 40 boxes do not render 40 labels up front. */
const LabelPreview: React.FC<LabelPreviewProps> = ({ label, getPng }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(
    typeof IntersectionObserver === 'undefined'
  );
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const zpl = isZplContentType(label.labelContentType);

  useEffect(() => {
    const el = frameRef.current;
    if (visible || !el) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    if (!zpl) {
      setSrc(`data:${label.labelContentType};base64,${label.labelBase64}`);
      return undefined;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setFailed(false);
    getPng(label)
      .then((blob) => {
        if (cancelled) return;
        if (!blob) {
          setFailed(true);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, zpl, label, getPng]);

  return (
    <div ref={frameRef} className={styles.labelPreview}>
      {src ? (
        label.labelContentType.includes('pdf') ? (
          <iframe title={`Label ${label.trackingNumber}`} src={src} />
        ) : (
          <img alt={`Label ${label.trackingNumber}`} src={src} />
        )
      ) : (
        <span className={styles.muted}>
          {failed
            ? 'Label preview unavailable. Use Download.'
            : 'Loading label preview…'}
        </span>
      )}
    </div>
  );
};

interface LabelCardsProps {
  labels: BookedLabel[];
  boxCount: number;
  getPng: (label: BookedLabel) => Promise<Blob | null>;
  onPrint: (label: BookedLabel) => void;
  onDownload: (label: BookedLabel) => void;
  printDisabled?: boolean;
}

const LabelCards: React.FC<LabelCardsProps> = ({
  labels,
  boxCount,
  getPng,
  onPrint,
  onDownload,
  printDisabled = false,
}) => (
  <>
    {labels.map((label) => (
      <div key={label.trackingNumber} className={styles.boxCard}>
        <div className={styles.boxCardTitle}>
          Box {label.sequence} of {boxCount}
        </div>
        <div className={styles.resultMetaLabel}>Tracking number</div>
        <div className={styles.resultMetaValue}>{label.trackingNumber}</div>
        <LabelPreview label={label} getPng={getPng} />
        <div className={styles.labelCardActions}>
          <Button
            aria-label={`Print label for box ${label.sequence} of ${boxCount}`}
            disabled={printDisabled}
            onClick={() => onPrint(label)}
          >
            Print
          </Button>
          <Button
            aria-label={`Download label for box ${label.sequence} of ${boxCount}`}
            onClick={() => onDownload(label)}
          >
            Download
          </Button>
        </div>
      </div>
    ))}
  </>
);

export default LabelCards;
